import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Download, Eye, Pencil, Search, Trash2, Upload, Wrench, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import MobileCrudList from '../../component/common/MobileCrud';
import { KitField, KitSelect } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import BooleanPill from '../../component/common/BooleanPill';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import ServiceViewDialog from './View';

// Listagem paginada no servidor (`GET /services/all?page=`): filtros e o
// "Valor total" (soma de todos os serviços filtrados, não só da página) vêm da API.
const SEARCH_DEBOUNCE_MS = 400;
const MOBILE_PAGE_SIZE = 20;

const WrenchIcon = protoIcon('wrench');
const EyeIcon = protoIcon('eye');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');
const UploadIcon = protoIcon('upload');
const DownloadIcon = protoIcon('download');

export default function ServicesList() {
    const theme = useTheme();
    const isMobileShell = useIsMobileShell();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação; filtros em rascunho na folha.
    const [mobilePage, setMobilePage] = useState(0);
    const [draftFilters, setDraftFilters] = useState({ category: '', subcategory: '', external: '', recurring: '' });

    const [loading, setLoading] = useState(true);
    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState(null);
    const [serviceNameToDelete, setServiceNameToDelete] = useState('');
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [tableRows, setTableRows] = useState([]);
    // Paginação server-side: `page` é 0-based (DefaultTable); a API usa 1-based.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalCount, setTotalCount] = useState(0);
    const [totalValue, setTotalValue] = useState('0,00');
    const [reloadKey, setReloadKey] = useState(0);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState(null);
    const [messageSnackBar, setMessageSnackBar] = useState('');
    const [severity, setSeverity] = useState('success');
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState('');
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingSubcategories, setLoadingSubcategories] = useState(false);
    // '' = Todos, '1' = Sim (só externos), '0' = Não (só não-externos) —
    // mesmos 3 valores do <select id="fExternal"> de
    // `prototipo-lista-servicos.html`. Substitui o antigo switch binário
    // "Apenas terceirizados" (só cobria Todos/Sim), exigido pelo próprio item
    // do design (FRONTEND-TASKS.md "Serviço externo (Todos-Sim-Não)").
    const [externalFilter, setExternalFilter] = useState('');
    // '' = Todos, '1' = Somente recorrentes. Filtro no cliente (a listagem traz
    // `is_recurring`), igual ao filtro de serviço externo. Só existe com
    // "Usa veículo" ativo.
    const [recurringFilter, setRecurringFilter] = useState('');
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const { settings: enterpriseSettings } = useEnterpriseConfigContext();
    const usesVehicle = Boolean(enterpriseSettings?.os_use_vehicle);
    const onlyRecurring = usesVehicle && recurringFilter === '1';

    // Busca a página atual no servidor sempre que filtros/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = { page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (appliedSearch.trim()) params.search = appliedSearch.trim();
        if (selectedCategoryId) params.categoryId = selectedCategoryId;
        if (selectedSubcategoryId) params.subcategory = selectedSubcategoryId;
        if (externalFilter !== '') params.external = externalFilter;
        if (onlyRecurring) params.recurring = '1';

        setLoading(true);
        httpClient.get('/services/all', { params })
            .then((response) => {
                if (cancelled) return;
                const items = Array.isArray(response.data?.items) ? response.data.items : [];
                const total = response.data?.total || 0;
                // Página ficou além do fim (ex.: excluiu o último item da última página).
                if (!isMobileShell && items.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && requestPage > 0) {
                    setTableRows((prev) => [...prev, ...items.filter((item) => !prev.some((p) => p.id === item.id))]);
                } else {
                    setTableRows(items);
                }
                setTotalCount(total);
                setTotalValue(response.data?.total_value || '0,00');
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error fetching services:', error);
                setTableRows([]);
                setTotalCount(0);
                setTotalValue('0,00');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, appliedSearch, selectedCategoryId, selectedSubcategoryId, externalFilter, onlyRecurring, reloadKey, isMobileShell, mobilePage]);

    // Debounce da busca: aplica depois de uma pausa na digitação e volta à página 1.
    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(search);
            setPage(0);
            setMobilePage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const handleChangeSearch = (searchValue) => {
        setSearch(searchValue);
    }

    // Botão "Buscar": aplica a busca na hora (sem esperar o debounce).
    const handleApplyFilters = () => {
        setAppliedSearch(search);
        setPage(0);
        setReloadKey((k) => k + 1);
    };

    const handleExternalChange = (value) => {
        setExternalFilter(value);
        setPage(0);
    };

    const handleRecurringChange = (value) => {
        setRecurringFilter(value);
        setPage(0);
    };

    const handleSubcategoryChange = (value) => {
        setSelectedSubcategoryId(value);
        setPage(0);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const fetchCategories = async () => {
        try {
            setLoadingCategories(true);
            const response = await httpClient.get('/category-service/dropdown');
            const responseData = Array.isArray(response.data) ? response.data : [];
            setCategories(responseData);
            return responseData;
        } catch (error) {
            console.error('Error fetching service categories:', error);
            setCategories([]);
            return [];
        } finally {
            setLoadingCategories(false);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        if (!categoryId) {
            setSubcategories([]);
            setLoadingSubcategories(false);
            return [];
        }
        try {
            setLoadingSubcategories(true);
            const response = await httpClient.get(`/category-service/subcategory/dropdown?categoryId=${categoryId}`);
            const responseData = Array.isArray(response.data) ? response.data : [];
            setSubcategories(responseData);
            return responseData;
        } catch (error) {
            console.error('Error fetching service subcategories:', error);
            setSubcategories([]);
            return [];
        } finally {
            setLoadingSubcategories(false);
        }
    };

    const handleCategoryChange = (value) => {
        setSelectedCategoryId(value);
        setSelectedSubcategoryId('');
        setPage(0);
        fetchSubcategories(value);
    };

    const handleClearFilters = () => {
        setSearch('');
        setAppliedSearch('');
        setExternalFilter('');
        setRecurringFilter('');
        setMobilePage(0);
        handleCategoryChange('');
    };

    // Mobile: a folha Filtros aplica categoria, subcategoria, externo e recorrência juntos.
    const handleMobileApplyFilters = () => {
        setSelectedCategoryId(draftFilters.category);
        setSelectedSubcategoryId(draftFilters.subcategory);
        setExternalFilter(draftFilters.external);
        setRecurringFilter(draftFilters.recurring);
        setPage(0);
        setMobilePage(0);
        fetchSubcategories(draftFilters.category);
    };

    const handleMobileOpenFilters = () => {
        setDraftFilters({
            category: selectedCategoryId,
            subcategory: selectedSubcategoryId,
            external: externalFilter,
            recurring: recurringFilter,
        });
        fetchSubcategories(selectedCategoryId);
    };

    // "Novo serviço"/"Editar" navegam para o formulário de serviço em página
    // cheia (`/services/new`, `/services/edit/:id` — `ServiceForm`), mesmo
    // precedente adotado em Produtos ("formulário completo = página, não
    // drawer/dialog") — ver FRONTEND-TASKS.md item "Formulário de serviço".
    const handleNewService = () => {
        navigate('/services/new');
    };

    const handleEditService = (row) => {
        navigate(`/services/edit/${row.id}`);
    };

    const columns = [
        {
            name: 'Nome',
            path: 'name',
            render: (row) => (
                <Typography
                    component="span"
                    sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}
                    title={row.name}
                >
                    {row.name}
                </Typography>
            )
        },
        {
            name: 'Categoria',
            path: 'category_name',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.category_name || '—'}
                </Typography>
            )
        },
        {
            name: 'Subcategoria',
            path: 'subcategory_name',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.subcategory_name || '—'}
                </Typography>
            )
        },
        {
            name: 'Descrição',
            path: 'description',
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 240,
                        mx: 'auto',
                        fontSize: '13px',
                        color: 'text.secondary'
                    }}
                    title={row.description}
                >
                    {row.description || 'Sem descrição'}
                </Box>
            )
        },
        {
            name: 'Valor',
            path: 'price',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'success.main' }}>
                    R$ {row.price}
                </Typography>
            )
        },
        {
            name: 'Serviço externo',
            path: 'is_external',
            render: (row) => <BooleanPill value={!!row.is_external} />
        },
        {
            name: 'Custo externo',
            path: 'external_price',
            render: (row) => (
                row.is_external ? (
                    <Typography component="span" sx={{ fontSize: '13px', fontWeight: 600, color: 'warning.main' }}>
                        R$ {row.external_price ?? '0,00'}
                    </Typography>
                ) : (
                    <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                )
            )
        }
    ]

    const actions = [
        ...(hasPermission('service.view') ? [{
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    setSelectedServiceId(row.id);
                    setOpenViewDialog(true);
                },
                tooltip: 'Visualizar',
            }
        }] : []),
        ...(hasPermission('service.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    handleEditService(row);
                },
                tooltip: 'Editar',
            }
        }] : []),
        ...(hasPermission('service.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    setServiceToDelete(row.id);
                    setServiceNameToDelete(row.name);
                    setOpenDialogConfirmDelete(true);
                },
                tooltip: 'Excluir',
            }
        }] : []),
    ]

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleDeleteService = async () => {
        setLoading(true);
        try {
            const response = await httpClient.delete(`/services/${serviceToDelete}`);
            if (response.status === 204) {
                setReloadKey((k) => k + 1);
                setMessageSnackBar('Serviço excluído com sucesso');
                setOpenSnackBar(true);
                setSeverity('success');
            } else {
                setMessageSnackBar('Erro ao excluir serviço');
                setOpenSnackBar(true);
                setSeverity('error');
            }
            setOpenDialogConfirmDelete(false);
            setServiceToDelete(null);
            setServiceNameToDelete('');
        } catch (error) {
            console.error('Error deleting service:', error);
            setMessageSnackBar('Erro ao excluir serviço');
            setOpenSnackBar(true);
            setSeverity('error');
        } finally {
            setLoading(false);
        }
    }

    const handleExport = async () => {
        try {
            await httpClient.downloadFile('/services/export', {
                title: 'servicos',
                extension: 'csv',
                responseType: 'blob'
            });
        } catch (error) {
            console.error('Error exporting services:', error);
            setMessageSnackBar('Erro ao exportar serviços');
            setOpenSnackBar(true);
            setSeverity('error');
        }
    }

    const selectedCategoryName = categories.find((c) => String(c.id) === String(selectedCategoryId))?.name;
    const selectedSubcategoryName = subcategories.find((s) => String(s.id) === String(selectedSubcategoryId))?.name;

    // Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9), igual a
    // `pages/sales/List.js`/`pages/customers/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const deleteAndViewDialogs = (
        <>
            <DialogForm
                iconType={'warning'}
                open={openDialogConfirmDelete}
                title={'Atenção'}
                confirmButtonText={'Sim, Excluir'}
                content={`Tem certeza que deseja excluir o serviço "${serviceNameToDelete}"?`}
                secondaryContent={'Esta ação não pode ser desfeita. O serviço será removido permanentemente do sistema.'}
                onSubmit={() => {
                    handleDeleteService();
                }}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setServiceToDelete(null);
                    setServiceNameToDelete('');
                }}
                loading={loading}
                loadingText="Removendo..."
            />

            <ServiceViewDialog
                open={openViewDialog}
                onClose={() => {
                    setOpenViewDialog(false);
                    setSelectedServiceId(null);
                }}
                serviceId={selectedServiceId}
                canEdit={hasPermission('service.update')}
                onRequestEdit={(svc) => {
                    setOpenViewDialog(false);
                    setSelectedServiceId(null);
                    if (svc?.id) {
                        handleEditService(svc);
                    }
                }}
            />
        </>
    );

    // Shell mobile: lista do protótipo `servicos/lista.html` (mesmos dados, filtros e ações).
    if (isMobileShell) {
        const mobileMoreItems = [
            ...(hasPermission('service.import') ? [{ label: 'Importar planilha', icon: UploadIcon, onClick: () => navigate('/services/import') }] : []),
            ...(hasPermission('service.export') ? [{ label: 'Exportar', icon: DownloadIcon, onClick: handleExport }] : []),
        ];
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget={'Serviços'} openSnackbar={openSnackBar} message={messageSnackBar} setOpenSnackbar={setOpenSnackBar} severity={severity}>
                <MobileCrudList
                    items={tableRows}
                    getKey={(row) => row.id}
                    getRow={(row) => ({
                        icon: WrenchIcon,
                        tone: 'purple',
                        title: row.name,
                        sub: [row.category_name, row.subcategory_name, row.is_external ? 'Externo' : '', usesVehicle && row.is_recurring ? 'Recorrente' : ''].filter(Boolean).join(' · '),
                        end: <Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {row.price}</Box>,
                    })}
                    loading={loading}
                    search={search}
                    onSearchChange={handleChangeSearch}
                    searchPlaceholder="Buscar serviço…"
                    filter={{
                        active: Boolean(selectedCategoryId || selectedSubcategoryId || externalFilter || onlyRecurring),
                        onOpen: handleMobileOpenFilters,
                        onApply: handleMobileApplyFilters,
                        onClear: handleClearFilters,
                        children: (
                            <>
                                <KitField label="Categoria">
                                    <KitSelect
                                        value={draftFilters.category}
                                        onChange={(e) => {
                                            setDraftFilters((prev) => ({ ...prev, category: e.target.value, subcategory: '' }));
                                            fetchSubcategories(e.target.value);
                                        }}
                                        aria-label="Categoria"
                                    >
                                        <option value="">Todas</option>
                                        {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                                    </KitSelect>
                                </KitField>
                                <KitField label="Subcategoria">
                                    <KitSelect
                                        value={draftFilters.subcategory}
                                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, subcategory: e.target.value }))}
                                        disabled={!draftFilters.category || loadingSubcategories}
                                        aria-label="Subcategoria"
                                    >
                                        <option value="">{draftFilters.category ? 'Todas' : 'Selecione uma categoria'}</option>
                                        {subcategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                                    </KitSelect>
                                </KitField>
                                <KitField label="Serviço externo">
                                    <KitSelect
                                        value={draftFilters.external}
                                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, external: e.target.value }))}
                                        aria-label="Serviço externo"
                                    >
                                        <option value="">Todos</option>
                                        <option value="1">Sim</option>
                                        <option value="0">Não</option>
                                    </KitSelect>
                                </KitField>
                                {usesVehicle && (
                                    <KitField label="Recorrência">
                                        <KitSelect
                                            value={draftFilters.recurring}
                                            onChange={(e) => setDraftFilters((prev) => ({ ...prev, recurring: e.target.value }))}
                                            aria-label="Recorrência"
                                        >
                                            <option value="">Todos</option>
                                            <option value="1">Somente recorrentes</option>
                                        </KitSelect>
                                    </KitField>
                                )}
                            </>
                        ),
                    }}
                    emptyIcon={WrenchIcon}
                    emptyTitle="Nenhum serviço encontrado"
                    emptyText="Ajuste a busca ou os filtros para ver outros serviços."
                    hasMore={tableRows.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    fabLabel={hasPermission('service.create') ? 'Novo' : undefined}
                    onNew={handleNewService}
                    moreItems={mobileMoreItems}
                    rowTitle={(row) => row.name}
                    rowActions={(row) => [
                        ...(hasPermission('service.view') ? [{
                            label: 'Visualizar',
                            icon: EyeIcon,
                            onClick: () => {
                                setSelectedServiceId(row.id);
                                setOpenViewDialog(true);
                            },
                        }] : []),
                        ...(hasPermission('service.update') ? [{ label: 'Editar', icon: EditIcon, onClick: () => handleEditService(row) }] : []),
                        ...(hasPermission('service.delete') ? [{
                            label: 'Excluir',
                            icon: TrashIcon,
                            destructive: true,
                            onClick: () => {
                                setServiceToDelete(row.id);
                                setServiceNameToDelete(row.name);
                                setOpenDialogConfirmDelete(true);
                            },
                        }] : []),
                    ]}
                />
                {deleteAndViewDialogs}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Serviços'} openSnackbar={openSnackBar} message={messageSnackBar} setOpenSnackbar={setOpenSnackBar} severity={severity} >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ações (Importar/Exportar/Novo serviço). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <Wrench size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Serviços
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Consulte, edite e organize o catálogo de serviços oferecidos.
                            </Typography>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1}>
                        {hasPermission('service.import') && (
                            <Tooltip title="Importar Serviços" placement="top" arrow>
                                <Button
                                    variant="outlined"
                                    startIcon={<Upload size={15} strokeWidth={2} />}
                                    onClick={() => navigate('/services/import')}
                                >
                                    Importar
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission('service.export') && (
                            <Tooltip
                                title="Baixa CSV com colunas do cadastro, incluindo SERVIÇO EXTERNO e PREÇO CUSTO EXTERNO após É GENÉRICO."
                                placement="top"
                                arrow
                            >
                                <Button
                                    variant="outlined"
                                    startIcon={<Download size={15} strokeWidth={2} />}
                                    onClick={handleExport}
                                >
                                    Exportar
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission('service.create') && (
                            <NewItemButton label="Novo serviço" onClick={handleNewService} />
                        )}
                    </Stack>
                </Box>

                {/* report-toolbar — Categoria/Subcategoria (dependente)/Serviço externo/Buscar. */}
                <FilterToolbar
                    plain
                    activeCount={(selectedCategoryId ? 1 : 0) + (selectedSubcategoryId ? 1 : 0) + (externalFilter ? 1 : 0) + (usesVehicle && recurringFilter ? 1 : 0)}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 220px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome do serviço…"
                                value={search}
                                onChange={(e) => handleChangeSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box>
                            <TbLabel>Categoria</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <Select
                                    value={selectedCategoryId}
                                    displayEmpty
                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                    renderValue={(value) => (value ? (selectedCategoryName || '') : 'Todas')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {loadingCategories ? (
                                        <MenuItem disabled>Carregando...</MenuItem>
                                    ) : (
                                        categories.map((category) => (
                                            <MenuItem key={category.id} value={category.id}>
                                                {category.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Subcategoria</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 170 }} disabled={!selectedCategoryId || loadingSubcategories}>
                                <Select
                                    value={selectedSubcategoryId}
                                    displayEmpty
                                    onChange={(e) => handleSubcategoryChange(e.target.value)}
                                    renderValue={(value) => (value ? (selectedSubcategoryName || '') : 'Todas')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {loadingSubcategories ? (
                                        <MenuItem disabled>Carregando...</MenuItem>
                                    ) : (
                                        subcategories.map((subcategory) => (
                                            <MenuItem key={subcategory.id} value={subcategory.id}>
                                                {subcategory.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Serviço externo</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <Select
                                    value={externalFilter}
                                    displayEmpty
                                    onChange={(e) => handleExternalChange(e.target.value)}
                                    renderValue={(value) => (value === '1' ? 'Sim' : value === '0' ? 'Não' : 'Todos')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="1">Sim</MenuItem>
                                    <MenuItem value="0">Não</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {usesVehicle && (
                            <Box>
                                <TbLabel>Recorrência</TbLabel>
                                <FormControl size="small" sx={{ minWidth: 190 }}>
                                    <Select
                                        value={recurringFilter}
                                        displayEmpty
                                        onChange={(e) => handleRecurringChange(e.target.value)}
                                        renderValue={(value) => (value === '1' ? 'Somente recorrentes' : 'Todos')}
                                        sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        <MenuItem value="1">Somente recorrentes</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                </FilterToolbar>

                {/* chips-row — filtros ativos removíveis + contagem/total. */}
                <FilterChips>
                    {selectedCategoryId && (
                        <Chip
                            label={`Categoria: ${selectedCategoryName || ''}`}
                            size="small"
                            onDelete={() => handleCategoryChange('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {selectedSubcategoryId && (
                        <Chip
                            label={`Subcategoria: ${selectedSubcategoryName || ''}`}
                            size="small"
                            onDelete={() => handleSubcategoryChange('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {externalFilter !== '' && (
                        <Chip
                            label={`Serviço externo: ${externalFilter === '1' ? 'Sim' : 'Não'}`}
                            size="small"
                            onDelete={() => handleExternalChange('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {onlyRecurring && (
                        <Chip
                            label="Somente recorrentes"
                            size="small"
                            onDelete={() => handleRecurringChange('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {appliedSearch && (
                        <Chip
                            label={`Busca: "${appliedSearch}"`}
                            size="small"
                            onDelete={() => { setSearch(''); setAppliedSearch(''); setPage(0); }}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} serviço${totalCount === 1 ? '' : 's'} encontrado${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`Valor total: R$ ${totalValue}`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={tableRows}
                    iconsActions={actions}
                    loading={loading}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="serviços"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>

            {deleteAndViewDialogs}
        </PageScaffold>
    )
}
